import styled from 'styled-components'
import { RADOM_COLORS, WINDOW_SIZE } from '../../util/Constants'
import { Container } from '../../components/Animations'
import NumberInput from '../../components/NumberInput'
import { SwapStatus } from '../../state/Radom'

export const SwapContainer = styled.div`
  align-self: start;
  background: var(--surface, #ffffff);
  border: 1px solid var(--surface-pressed, #edf1f7);
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: fit-content;
  max-width: 380px;
  padding: 16px;
  position: sticky;
  top: 86px;
  width: 100%;

  @media (max-width: ${WINDOW_SIZE.LAPTOP}) {
    max-width: none;
    position: static;
  }
`

export const ChartContainer = styled.div`
  background: var(--surface, #ffffff);
  border: 1px solid var(--surface-pressed, #edf1f7);
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  width: 100%;
`

export const ToogleContainer = styled.div`
  align-items: center;
  background: var(--surface-hover, #f5f7fb);
  border: 1px solid var(--border-soft, #eceff3);
  border-radius: 12px;
  display: flex;
  flex-direction: row;
  gap: 6px;
  height: 44px;
  padding: 4px;
  position: relative;
  width: 100%;
`

export const Slider = styled.div<{ $selected: 'buy' | 'sell' }>`
  background: var(--surface, #ffffff);
  border-radius: 9px;
  box-shadow: 0 1px 2px var(--shadow-color, rgba(16, 24, 40, 0.05)), 0 1px 3px var(--shadow-color, rgba(16, 24, 40, 0.1));
  height: calc(100% - 8px);
  left: 4px;
  pointer-events: none;
  position: absolute;
  top: 4px;
  transform: translateX(${({ $selected }) => $selected === 'sell' ? 'calc(100% + 6px)' : '0'});
  transition: transform 0.24s cubic-bezier(0.4, 0, 0.2, 1);
  width: calc(50% - 7px);
`

export const ToggleButton = styled.button<{ $selected: boolean }>`
  background: transparent;
  border: none;
  border-radius: 9px;
  color: ${({ $selected }) => $selected ? 'var(--ink-strong, #121722)' : 'var(--text-muted, #6b7280)'};
  cursor: pointer;
  flex: 1;
  font-size: 13.5px;
  font-weight: ${({ $selected }) => $selected ? 500 : 450};
  height: 100%;
  position: relative;
  transition: color 0.2s ease;
  z-index: 1;

  &:hover {
    color: var(--ink-strong, #121722);
  }
`

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;

  .side-label {
    color: var(--text-muted, #6f7888);
    font-size: 13px;
    font-weight: 500;
  }
`

/** The amount's value in the reporting currency, under the amount. */
export const FiatValue = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  min-height: 18px;
`

/** Swaps the two sides; sits on the seam between the cards. */
export const FlipButton = styled.button`
  align-items: center;
  align-self: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border-soft, #eceff3);
  border-radius: 12px;
  color: var(--ink, #20242d);
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: center;
  margin: -18px 0;
  position: relative;
  transition: transform 120ms ease, background 120ms ease;
  width: 40px;
  z-index: 1;

  &:hover:not(:disabled) {
    background: var(--surface-muted, #f8fafc);
    transform: translateY(1px);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`

export const GrayWrapper = styled.div`
  background: var(--surface-muted, #f8fafc);
  border: 1px solid var(--border-soft, #eceff3);
  border-radius: 12px;
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: space-between;
  padding: 12px;
  width: 100%;
`

export const Input = styled(NumberInput)`
  && {
    align-self: flex-start;
    background: transparent;
    border: none;
    box-shadow: none;
    color: var(--ink-strong, #121722);
    font-size: 26px;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
    min-height: 0;
    padding: 0;
    width: 100%;

    &::placeholder {
      color: var(--text-disabled, #a4aab5);
    }

    &:disabled {
      color: var(--ink, #20242d);
      opacity: 1;
      -webkit-text-fill-color: var(--ink, #20242d);
    }
  }
`

export const AssetView = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 7px;

  span {
    color: var(--ink, #20242d);
    font-size: 13.5px;
    font-weight: 500;
  }

  img {
    border-radius: 50%;
    height: 20px;
    width: 20px;
  }
`

export const RateNote = styled.span`
  font-size: 12px;
  color: var(--text-muted, #6b6b6b);
`

export const ValueView = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  span:first-child {
    color: var(--text-muted, #6f7888);
    font-size: 12px;
  }

  span:last-child {
    color: var(--ink, #20242d);
    font-size: 12px;
    font-variant-numeric: tabular-nums;
  }
`

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

export const HeaderBar = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  width: 100%;
`

export const HeaderLeft = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  min-width: 0;
`

export const HeaderRight = styled.div`
  flex: 0 0 380px;

  @media (max-width: ${WINDOW_SIZE.LAPTOP}) {
    display: none;
  }
`

export const HeaderStats = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
`

export const HeaderStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
`

export const HeaderStatLabel = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.03em;
  line-height: 1.25;
  text-transform: uppercase;
`

export const HeaderStatValue = styled.span<{ $trend?: 'up' | 'down' }>`
  align-items: center;
  color: var(--ink-strong, #121722);
  display: flex;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  gap: 5px;
  line-height: 1.12;
  white-space: nowrap;
  width: 120px;

  &::before {
    content: '';
    display: ${({ $trend }) => $trend ? 'block' : 'none'};
    width: 0;
    height: 0;
    border-left: 3.5px solid transparent;
    border-right: 3.5px solid transparent;
    ${({ $trend }) => $trend === 'up'
    ? 'border-bottom: 5px solid var(--trend-up, #26a17b);'
    : 'border-top: 5px solid var(--trend-down, #be3832);'}
  }
`

export const TierSub = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 11px;

  &::before {
    content: '·';
    margin-right: 5px;
    opacity: 0.6;
  }
`

export const HeaderDivider = styled.div`
  background: linear-gradient(180deg, transparent 0%, var(--border, #e3e7ef) 50%, transparent 100%);
  height: 30px;
  width: 1px;
`

export const HeaderBackLabel = styled.span`
  align-items: center;
  color: var(--text-muted, #6f7888);
  cursor: pointer;
  display: flex;
  flex-direction: row;
  font-size: 14px;
  gap: 4px;

  svg {
    fill: var(--icon-muted, #747894);
    transform: rotate(90deg);
    width: 16px;
  }
`

export const ContainerWrapper = styled(Container)`
  align-items: center;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  max-width: 1280px;
  width: 100%;
`

export const TickerSelector = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: fit-content;
`

export const PriceWrapper = styled.div<{ $positive: boolean }>`
  align-items: flex-end;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;

  span:first-child {
    color: var(--ink-strong, #121722);
    font-size: 32px;
    font-variant-numeric: tabular-nums;
    font-weight: 550;
    letter-spacing: -0.015em;
    line-height: 1.1;
  }

  span:last-child {
    color: ${({ $positive }) => $positive ? 'var(--trend-up, #26a17b)' : 'var(--trend-down, #be3832)'};
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    font-weight: 500;
    line-height: 1.6;
  }
`

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - 40px);
  width: 550px;
`

export const ListHeader = styled.div`
  background: var(--surface, #ffffff);
  position: sticky;
  top: 0;
  z-index: 1;
`

export const SearchRow = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--border-soft, #eceff3);
  display: flex;
  gap: 8px;
  padding: 10px 14px;
`

export const SearchInput = styled.input`
  background: transparent;
  border: none;
  color: var(--ink, #20242d);
  flex: 1;
  font-family: var(--font-sans);
  font-size: 13px;
  outline: none;

  &::placeholder {
    color: ${RADOM_COLORS.INPUT_PLACEHOLDER};
  }
`

export const TableHeader = styled.div`
  background: var(--surface-subtle, #f9fafb);
  border-bottom: 1px solid var(--border-soft, #eceff3);
  color: var(--text-muted, #6f7888);
  display: grid;
  font-size: 10px;
  font-weight: 500;
  grid-template-columns: 1fr 1fr 90px;
  letter-spacing: 0.04em;
  padding: 7px 14px;

  > * {
    text-align: left;
  }

  > *:last-child {
    text-align: right;
  }
`

export const ListWrapper = styled.div`
`

export const Row = styled.div`
  align-items: center;
  cursor: pointer;
  display: grid;
  grid-template-columns: 1fr 1fr 90px;
  padding: 7px 14px;
  transition: 0.12s ease background;

  &:not(:last-child) {
    border-bottom: 1px solid var(--hairline-soft, #f1f4f9);
  }

  &:hover {
    background: var(--surface-hover, #f5f7fb);
  }
`

export const PairCell = styled.div`
  align-items: center;
  display: flex;
  gap: 9px;
`

export const PairLogo = styled.img`
  border: 1px solid var(--border-soft, #eceff3);
  border-radius: 50%;
  height: 24px;
  width: 24px;

  &:first-child {
    position: relative;
    z-index: 1;
  }

  &:last-child {
    margin-left: -8px;
  }
`

export const PairLogos = styled.div`
  display: flex;
  isolation: isolate;
`

export const PairName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

export const PairLabel = styled.span`
  color: var(--ink, #20242d);
  font-size: 13px;
  font-weight: 500;
`

export const PairSub = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 12px;
`

export const PriceCell = styled.div`
  align-items: baseline;
  color: var(--ink, #20242d);
  display: flex;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  gap: 3px;
`

export const PriceUnit = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 10px;
`

export const ChangeCell = styled.span<{ $positive: boolean }>`
  color: ${({ $positive }) => $positive ? 'var(--trend-up, #26a17b)' : 'var(--trend-down, #be3832)'};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  text-align: right;
`

export const VolumeCell = styled.div`
  align-items: baseline;
  color: var(--text-muted, #6f7888);
  display: flex;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  gap: 2px;
`

export const VolumeUnit = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 10px;
`

export const EmptyState = styled.div`
  align-items: center;
  color: var(--text-muted, #6f7888);
  display: flex;
  font-size: 13px;
  justify-content: center;
  padding: 32px 0;
  text-align: center;
`

export const ContentLayout = styled.div`
  align-items: start;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) 380px;
  width: 100%;

  @media (max-width: ${WINDOW_SIZE.LAPTOP}) {
    grid-template-columns: 1fr;
  }
`

export const ChartColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  width: 100%;
`

export const SectionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

export const SectionLabel = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.05em;
  line-height: 1.25;
  padding: 0 4px;
  text-transform: uppercase;
`

export const ChartHeader = styled.div`
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
`

export const TickerLogoPair = styled.div`
  align-items: center;
  border-radius: 999px;
  display: flex;
  flex-shrink: 0;
  transition: 0.2s ease box-shadow;
`

export const TickerLogoFirst = styled.img`
  border: 2px solid var(--surface, #ffffff);
  border-radius: 50%;
  height: 32px;
  position: relative;
  width: 32px;
  z-index: 0;
`

export const TickerLogoSecond = styled.img`
  border: 2px solid var(--surface, #ffffff);
  border-radius: 50%;
  box-sizing: border-box;
  height: 32px;
  margin-left: -8px;
  position: relative;
  width: 32px;
  z-index: 1;
`

export const TickerTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const TickerPairName = styled.span`
  align-items: center;
  color: var(--ink-strong, #121722);
  display: flex;
  font-size: 17px;
  font-weight: 500;
  gap: 6px;
  letter-spacing: -0.01em;
  line-height: 20px;
`

export const TickerNetworkName = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.06em;
  line-height: 12px;
  text-transform: uppercase;
`

export const TransactionTypeCell = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`

export const TransactionLogoStack = styled.div`
  display: flex;
`

export const TransactionLogoFirst = styled.img`
  border: 1.5px solid var(--surface, #ffffff);
  border-radius: 50%;
  height: 22px;
  width: 22px;
  z-index: 1;
`

export const TransactionLogoSecond = styled.img`
  border: 1.5px solid var(--surface, #ffffff);
  border-radius: 50%;
  height: 22px;
  margin-left: -6px;
  width: 22px;
`

export const TransactionLabel = styled.span`
  font-size: 13px;
`

const statusPill: Record<SwapStatus, string> = {
  Pending: 'background: var(--pill-amber-bg, #fff3df); color: var(--pill-amber-ink, #a85014);',
  Finalized: 'background: var(--pill-green-bg, #e8f7ef); color: var(--pill-green-ink, #16724b);',
  Failed: 'background: var(--pill-red-bg, #ffe9ec); color: var(--pill-red-ink, #c51735);',
  Initiated: 'background: var(--pill-neutral-bg, #f3f5f9); color: var(--pill-neutral-ink, #687183);'
}

export const StatusBadge = styled.span<{ $status: SwapStatus }>`
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 500;
  line-height: 1;
  padding: 5px 9px;
  white-space: nowrap;
  ${({ $status }) => statusPill[$status]}
`

export const RateText = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`

export const AmountsCell = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const NegativeAmount = styled.span`
  color: var(--trend-down, #be3832);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

export const PositiveAmount = styled.span`
  color: var(--trend-up, #26a17b);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

export const DateText = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 13px;
`

export const Footer = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: space-between;
`

export const BalanceView = styled.div`
  color: var(--text-faint, #8a919e);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

export const BalanceWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: center;
`

export const MaxButton = styled.button`
  background: var(--primary-btn-bg, #171717);
  border: none;
  border-radius: 5px;
  color: var(--primary-btn-ink, #ffffff);
  cursor: pointer;
  font-size: 10.5px;
  font-weight: 500;
  padding: 3px 7px;
  transition: 0.16s ease opacity;

  &:hover {
    opacity: 0.82;
  }
`

export const ConfirmSummary = styled.div`
  border: 1px solid var(--border-soft, #eceff3);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`

export const ConfirmAssetRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 14px 16px;
`

export const ConfirmAssetMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const ConfirmAssetLabel = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`

export const ConfirmAssetAmount = styled.div`
  align-items: center;
  color: var(--ink-strong, #121722);
  display: flex;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  gap: 8px;

  img {
    border-radius: 50%;
    height: 22px;
    width: 22px;
  }
`

export const ConfirmDivider = styled.div`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border-soft, #eceff3);
  border-radius: 50%;
  display: flex;
  height: 26px;
  justify-content: center;
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;

  svg {
    fill: var(--icon-muted, #747894);
    width: 10px;
  }
`

export const ConfirmNote = styled.div`
  background: var(--surface-muted, #f8fafc);
  border-radius: 10px;
  color: var(--text-muted, #6f7888);
  font-size: 12px;
  line-height: 1.5;
  padding: 10px 13px;
`
